/**
 * **La captura de la pantalla y «señalar un elemento»** de «Reportar mejora» (0182).
 *
 * La captura la pinta `modern-screenshot` (MIT; ya estaba en el proyecto para el PDF del Cotizador y el
 * owner la aprobó el 20-oct para esto): se descarga solo al usarla. Se toma **lo que se ve** —el ancho y
 * el alto de la ventana, desde donde está desplazada— en JPEG; si pasa de 3 MB se repite más pequeña.
 * Lo marcado con `data-sin-captura` (el menú, el aviso de «señalar») no sale en la foto.
 *
 * Señalar: se resalta el elemento bajo el ratón (o el dedo) y el primer clic lo elige, sin llegar a la
 * pantalla; `Esc` cancela. De él queda una descripción legible («botón «Guardar» en «Cotización»») y una
 * captura con el elemento marcado.
 */

/** El tope del servidor (`user_report.screenshot`). */
export const MAX_BYTES = 3 * 1024 * 1024;

const SIN_CAPTURA = "data-sin-captura";

function bytesDe(dataUrl: string): number {
  return Math.floor(((dataUrl.length - dataUrl.indexOf(",") - 1) * 3) / 4);
}

/** La pantalla tal como se ve, como `data:image/jpeg;base64,…`. */
export async function capturarPantalla(): Promise<string> {
  const { domToJpeg } = await import("modern-screenshot");
  const ancho = window.innerWidth;
  const alto = window.innerHeight;
  const tomar = (escala: number, calidad: number) =>
    domToJpeg(document.body, {
      width: ancho,
      height: alto,
      scale: escala,
      quality: calidad,
      backgroundColor: getComputedStyle(document.body).backgroundColor || "#ffffff",
      style: window.scrollY || window.scrollX ? { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` } : undefined,
      filter: (nodo) => !(nodo instanceof Element && nodo.hasAttribute(SIN_CAPTURA)),
    });
  let imagen = await tomar(Math.min(1, 1600 / ancho), 0.75);
  if (bytesDe(imagen) > MAX_BYTES) imagen = await tomar(Math.min(0.6, 1000 / ancho), 0.55);
  if (bytesDe(imagen) > MAX_BYTES) throw new Error("La captura salió muy pesada: elige una imagen más pequeña.");
  return imagen;
}

/** Una imagen elegida a mano, como `data:` (si no se pudo capturar sola). */
export function leerImagen(archivo: File): Promise<string> {
  return new Promise((resolver, fallar) => {
    if (archivo.size > MAX_BYTES) {
      fallar(new Error("La imagen pasa de 3 MB: elige una más pequeña."));
      return;
    }
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result));
    lector.onerror = () => fallar(new Error("La imagen no se pudo leer: elige otra."));
    lector.readAsDataURL(archivo);
  });
}

const TIPO: Record<string, string> = {
  button: "botón", a: "enlace", input: "casilla", select: "lista", textarea: "casilla de texto", img: "imagen",
  table: "tabla", tr: "fila", td: "celda", th: "columna", h1: "título", h2: "título", h3: "título", label: "etiqueta",
};

function texto(el: Element | null | undefined): string {
  if (!el) return "";
  const propio = el.getAttribute("aria-label") || el.getAttribute("title") || el.getAttribute("placeholder") || el.getAttribute("alt");
  return (propio || (el as HTMLElement).innerText || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
}

/** «botón «Guardar» en «Cotización»»: lo que se señaló, en palabras. */
export function describir(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const tipo = TIPO[tag] ?? (el.getAttribute("role") || "zona");
  const nombre = texto(el);
  const zona = el.closest("[role=dialog], section[aria-label], [aria-label][role=region], main");
  const dondeTexto = zona?.getAttribute("aria-label") || texto(zona?.querySelector("h1, h2"));
  const partes = [nombre ? `${tipo} «${nombre}»` : tipo, dondeTexto && dondeTexto !== nombre ? `en «${dondeTexto}»` : ""];
  return partes.filter(Boolean).join(" ").slice(0, 500);
}

/**
 * Espera a que la persona toque un elemento. Devuelve el elemento elegido (marcado mientras se toma la
 * captura: llamar a `soltar` al terminar) o `null` si canceló con `Esc`.
 */
export function senalar(): Promise<{ elemento: Element; soltar: () => void } | null> {
  return new Promise((resolver) => {
    const aviso = document.createElement("div");
    aviso.setAttribute(SIN_CAPTURA, "");
    aviso.setAttribute("role", "status");
    aviso.textContent = "Toca lo que está mal · Esc para cancelar";
    aviso.className = "fixed top-3 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-surface shadow-soft";
    document.body.append(aviso);
    let actual: HTMLElement | null = null;
    let antes = "";

    const marcar = (el: HTMLElement | null) => {
      if (actual) actual.style.outline = antes;
      actual = el;
      antes = el?.style.outline ?? "";
      if (el) el.style.outline = "3px solid #d64545";
    };
    const sobre = (e: Event) => {
      const el = e.target instanceof HTMLElement && !aviso.contains(e.target) ? e.target : null;
      marcar(el);
    };
    const terminar = (elegido: HTMLElement | null) => {
      document.removeEventListener("pointerover", sobre, true);
      document.removeEventListener("click", tocar, true);
      document.removeEventListener("keydown", tecla, true);
      aviso.remove();
      if (!elegido) {
        marcar(null);
        resolver(null);
        return;
      }
      resolver({ elemento: elegido, soltar: () => marcar(null) });
    };
    const tocar = (e: MouseEvent) => {
      const el = e.target instanceof HTMLElement && !aviso.contains(e.target) ? e.target : null;
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      marcar(el);
      terminar(el);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      terminar(null);
    };
    document.addEventListener("pointerover", sobre, true);
    document.addEventListener("click", tocar, true);
    document.addEventListener("keydown", tecla, true);
  });
}
